import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppContext } from '../App';

const Header = () => {
  const {
    activeSection,
    setActiveSection,
    language,
    setLanguage,
    scrollToSection,
    user,
    signOut,
    setIsAuthModalOpen,
    setAuthMode,
    cartItemCount,
    setIsCartOpen,
    showNotification
  } = useAppContext();

  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const langMenuRef = useRef(null);

  const location = useLocation();

  const navItems = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'products-section', label: 'Chemicals', path: '/products' },
    { id: 'factories', label: 'Suppliers', path: '/suppliers' },
    { id: 'about', label: 'About', path: '/about' },
    { id: 'contact', label: 'Contact', path: '/contact' }
  ];

  const languages = [
    { code: 'EN', label: 'English' },
    { code: 'ES', label: 'Español' },
    { code: 'ZH', label: '中文' },
    { code: 'JA', label: '日本語' }
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close lang menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleNavClick = (id) => {
    setActiveSection(id);
    setMobileMenuOpen(false);

    // Smooth scroll to section
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setLangMenuOpen(false);
    showNotification(`Language changed to ${languages.find(l => l.code === langCode)?.label}`, 'info');
  };

  const handleSignIn = () => {
    setAuthMode('signin');
    setIsAuthModalOpen(true);
    setMobileMenuOpen(false);
  };

  const handleSignUp = () => {
    setAuthMode('signup');
    setIsAuthModalOpen(true);
    setMobileMenuOpen(false);
  };

  const handleSignOut = () => {
    signOut();
    showNotification('Signed out successfully', 'info');
  };

  return (
    <>
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="logo" onClick={() => handleNavClick('home')}>
              <div className="logo-icon">
                <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="flaskGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>
                  <path d="M14 8 L14 16 L8 32 C7 34 8 36 10 36 L30 36 C32 36 33 34 32 32 L26 16 L26 8" stroke="url(#flaskGradient)" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="13" y="4" width="14" height="4" rx="1" fill="url(#flaskGradient)"/>
                  <path d="M11 28 Q20 24 29 28 L29 32 Q20 34 11 32 Z" fill="url(#flaskGradient)" opacity="0.4"/>
                  <circle cx="16" cy="30" r="2" fill="#60a5fa" opacity="0.8"/>
                  <circle cx="22" cy="28" r="1.5" fill="#60a5fa" opacity="0.6"/>
                  <circle cx="24" cy="32" r="1" fill="#60a5fa" opacity="0.7"/>
                </svg>
              </div>
              <div className="logo-text">
                <span className="logo-main">Asia</span>
                <span className="logo-sub">Quimicos</span>
              </div>
            </div>

            <nav className={`nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              {/* Mobile-only auth buttons */}
              <div className="mobile-auth-buttons">
                {user ? (
                  <button className="btn btn-outline mobile-auth-btn" onClick={handleSignOut}>
                    Sign Out
                  </button>
                ) : (
                  <>
                    <button className="btn btn-outline mobile-auth-btn" onClick={handleSignIn}>
                      Sign In
                    </button>
                    <button className="btn btn-primary mobile-auth-btn" onClick={handleSignUp}>
                      Get Started
                    </button>
                  </>
                )}
              </div>
            </nav>

            <div className="header-actions">
              <div className="language-selector" ref={langMenuRef}>
                <button
                  className="lang-toggle"
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  aria-label="Select language"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1-4 10" />
                  </svg>
                  <span>{language}</span>
                  <svg className={`chevron ${langMenuOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 11L3 6h10l-5 5z" />
                  </svg>
                </button>
                {langMenuOpen && (
                  <div className="lang-menu">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        className={`lang-option ${language === lang.code ? 'active' : ''}`}
                        onClick={() => handleLanguageChange(lang.code)}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                className="cart-btn"
                onClick={() => setIsCartOpen(true)}
                aria-label="Shopping cart"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
              </button>

              {/* Desktop auth buttons */}
              <div className="desktop-auth">
                {user ? (
                  <div className="user-menu">
                    <span className="user-name">{user.name || user.email?.split('@')[0]}</span>
                    <button className="btn btn-outline header-btn" onClick={handleSignOut}>
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <>
                    <button className="btn btn-outline header-btn" onClick={handleSignIn}>
                      Sign In
                    </button>
                    <button className="btn btn-primary header-btn" onClick={handleSignUp}>
                      Get Started
                    </button>
                  </>
                )}
              </div>

              <button
                className="mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {mobileMenuOpen ? (
                    <>
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </>
                  ) : (
                    <>
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        <style jsx>{`
          .header {
            position: sticky;
            top: 0;
            z-index: 100;
            background: rgba(15, 23, 42, 0.95);
            backdrop-filter: blur(20px);
            border-bottom: 1px solid rgba(59, 130, 246, 0.15);
            padding: var(--space-md) 0;
            transition: all var(--transition-base);
          }

          .header.scrolled {
            background: rgba(15, 23, 42, 0.98);
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }

          .header-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--space-lg);
          }

          .logo {
            display: flex;
            align-items: center;
            gap: var(--space-sm);
            cursor: pointer;
            transition: opacity var(--transition-fast);
          }

          .logo:hover {
            opacity: 0.9;
          }

          .logo-icon {
            width: 36px;
            height: 36px;
            flex-shrink: 0;
          }

          .logo-icon svg {
            width: 100%;
            height: 100%;
          }

          .logo-text {
            display: flex;
            align-items: baseline;
            gap: 4px;
            font-size: 1.25rem;
            font-weight: 700;
            letter-spacing: 0.02em;
          }

          .logo-main {
            color: #60a5fa;
          }

          .logo-sub {
            color: #94a3b8;
            font-weight: 400;
          }

          .nav {
            display: flex;
            gap: 4px;
          }

          .nav-item {
            font-size: 0.875rem;
            font-weight: 500;
            padding: 8px 14px;
            background: transparent;
            border: none;
            color: #cbd5e1;
            cursor: pointer;
            transition: all 150ms ease;
            border-radius: 8px;
            position: relative;
          }

          .nav-item:hover {
            color: #60a5fa;
            background: rgba(59, 130, 246, 0.1);
          }

          .nav-item.active {
            color: #60a5fa;
            background: rgba(59, 130, 246, 0.15);
          }

          .nav-item.active::after {
            content: '';
            position: absolute;
            bottom: 4px;
            left: 50%;
            transform: translateX(-50%);
            width: 16px;
            height: 2px;
            background: #60a5fa;
            border-radius: 1px;
          }

          .header-actions {
            display: flex;
            align-items: center;
            gap: var(--space-sm);
          }

          .language-selector {
            position: relative;
          }

          .lang-toggle {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            color: #e2e8f0;
            cursor: pointer;
            transition: all 150ms ease;
            font-size: 0.875rem;
          }

          .lang-toggle:hover {
            background: rgba(255, 255, 255, 0.1);
            border-color: rgba(59, 130, 246, 0.5);
          }

          .chevron {
            transition: transform 150ms ease;
          }

          .chevron.open {
            transform: rotate(180deg);
          }

          .lang-menu {
            position: absolute;
            top: calc(100% + 8px);
            right: 0;
            background: rgba(30, 41, 59, 0.98);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 4px;
            min-width: 130px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
            animation: fadeIn 0.2s ease;
            z-index: 200;
          }

          .lang-option {
            display: block;
            width: 100%;
            padding: 8px 12px;
            background: transparent;
            border: none;
            color: #cbd5e1;
            text-align: left;
            cursor: pointer;
            border-radius: 6px;
            transition: all 150ms ease;
            font-size: 0.875rem;
          }

          .lang-option:hover,
          .lang-option.active {
            background: rgba(59, 130, 246, 0.2);
            color: #60a5fa;
          }

          .cart-btn {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            color: #e2e8f0;
            cursor: pointer;
            transition: all 150ms ease;
          }

          .cart-btn:hover {
            background: rgba(255, 255, 255, 0.1);
            border-color: rgba(59, 130, 246, 0.5);
            color: #60a5fa;
          }

          .cart-badge {
            position: absolute;
            top: -6px;
            right: -6px;
            min-width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #3b82f6;
            color: white;
            font-size: 0.7rem;
            font-weight: 600;
            border-radius: 50%;
            padding: 0 5px;
            border: 2px solid #0f172a;
          }

          .desktop-auth {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .user-menu {
            display: flex;
            align-items: center;
            gap: var(--space-sm);
          }

          .user-name {
            color: #94a3b8;
            font-size: 0.875rem;
            max-width: 100px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .header-btn {
            padding: 8px 16px;
            font-size: 0.8rem;
            white-space: nowrap;
          }

          .mobile-menu-btn {
            display: none;
            width: 40px;
            height: 40px;
            align-items: center;
            justify-content: center;
            background: transparent;
            border: none;
            color: #e2e8f0;
            cursor: pointer;
            border-radius: 8px;
            transition: background 150ms ease;
          }

          .mobile-menu-btn:hover {
            background: rgba(255, 255, 255, 0.1);
          }

          .mobile-auth-buttons {
            display: none;
            flex-direction: column;
            gap: 8px;
            padding-top: var(--space-md);
            margin-top: var(--space-md);
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }

          .mobile-auth-btn {
            width: 100%;
            justify-content: center;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-8px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @media (max-width: 1024px) {
            .mobile-menu-btn {
              display: flex;
            }

            .nav {
              position: fixed;
              top: 68px;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(15, 23, 42, 0.98);
              padding: var(--space-lg);
              flex-direction: column;
              gap: 4px;
              transform: translateX(-100%);
              transition: transform 300ms ease;
              overflow-y: auto;
              z-index: 99;
            }

            .nav.mobile-open {
              transform: translateX(0);
            }

            .nav-item {
              padding: var(--space-md);
              font-size: 1rem;
              text-align: left;
              border-radius: 8px;
            }

            .nav-item.active::after {
              display: none;
            }

            .mobile-auth-buttons {
              display: flex;
            }

            .desktop-auth {
              display: none;
            }
          }

          @media (max-width: 768px) {
            .language-selector {
              display: none;
            }

            .logo-text {
              font-size: 1.1rem;
            }

            .header-btn {
              padding: 6px 12px;
              font-size: 0.75rem;
            }
          }

          @media (max-width: 480px) {
            .logo-text {
              display: none;
            }

            .logo-icon {
              width: 40px;
              height: 40px;
            }
          }
        `}</style>
      </header>

      {/* Mobile overlay styles */}
      <style jsx global>{`
        .mobile-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 98;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default Header;
